import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Breadcrumbs, Link, Typography } from "@mui/material";

export interface BreadcrumbItem {
  label: string;
  /** Route to link to; omit for plain text (section names, the current page). */
  to?: string;
}

export interface PageHeaderProps {
  /** Page title (h1, rendered in the h3 page-title style). */
  title: React.ReactNode;
  /** One-line description under the title. */
  subtitle?: React.ReactNode;
  /** e.g. [{ label: "Payments" }, { label: "Bills", to: "/bills" }, { label: "Bill 123" }] */
  breadcrumbs?: BreadcrumbItem[];
  /** Buttons or status shown at the right of the title. */
  actions?: React.ReactNode;
  /** Browser tab title ("<value> | CSI Pay"). Defaults to `title` when it is a string. */
  helmetTitle?: string;
}

/**
 * Standard top of every screen: tab title, breadcrumbs, title, subtitle and actions.
 *
 *   <PageHeader title="Bills" subtitle="Every bill CSI Pay has received."
 *     breadcrumbs={[{ label: "Payments" }, { label: "Bills" }]}
 *     actions={<Button variant="contained">Convert to check</Button>} />
 */
export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  helmetTitle,
}: PageHeaderProps) {
  const tabTitle =
    helmetTitle ?? (typeof title === "string" ? title : undefined);

  return (
    <Box sx={{ mb: 4 }}>
      {tabTitle && <Helmet title={tabTitle} />}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          aria-label="Breadcrumb"
          separator="›"
          sx={{
            mb: 2,
            fontSize: "0.78125rem",
            color: "text.secondary",
            // The › separator is decoration, so it alone stays muted.
            "& .MuiBreadcrumbs-separator": { mx: 1, color: "text.disabled" },
          }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return crumb.to && !isLast ? (
              <Link
                key={`${crumb.label}-${index}`}
                component={RouterLink}
                to={crumb.to}
                color="primary"
                underline="hover"
                sx={{ fontSize: "inherit" }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Box
                component="span"
                key={`${crumb.label}-${index}`}
                aria-current={isLast ? "page" : undefined}
              >
                {crumb.label}
              </Box>
            );
          })}
        </Breadcrumbs>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
          <Typography variant="h3" component="h1">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;
